import { OrderProduct } from "../interfaces/Order";

/**
 * Removes all properties with value `undefined` from an object recursively.
 * This is necessary because Firestore does not support `undefined` values.
 */
export function removeUndefined(obj: any): any {
  if (typeof obj === "number" && isNaN(obj)) {
    return 0;
  }

  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  // Preserve Dates and Firestore Timestamps
  if (obj instanceof Date || (typeof obj.toDate === "function")) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }

  return Object.keys(obj).reduce((acc: any, key) => {
    const value = obj[key];
    if (value !== undefined) {
      acc[key] = removeUndefined(value);
    }
    return acc;
  }, {});
}

/**
 * Groups a list of products by productId, variantId and additions.
 * Used for merging items in the cashier view and receipts.
 */
export function groupProducts(products: OrderProduct[]): OrderProduct[] {
  const grouped: OrderProduct[] = [];

  (products || []).forEach((p) => {
    const existing = grouped.find((g) => {
      if (g.productId !== p.productId || g.variantId !== p.variantId) return false;
      const gAddIds = (g.additions || []).map((a) => a.id).sort().join(",");
      const pAddIds = (p.additions || []).map((a) => a.id).sort().join(",");
      return gAddIds === pAddIds;
    });

    if (existing) {
      existing.quantity += p.quantity;
      existing.total = (existing.total || 0) + (p.total ?? (p.price * p.quantity));
    } else {
      grouped.push({ ...p });
    }
  });

  return grouped;
}

/**
 * Ensures an order object has a valid products array populated.
 * If order.products is empty or missing, reconstructs products from order.pizzas,
 * order.otherProductNames, and order.additionNames.
 */
export function getNormalizedProducts(order: any): OrderProduct[] {
  if (!order) return [];
  if (order.products && Array.isArray(order.products) && order.products.length > 0) {
    return order.products;
  }
  const products: OrderProduct[] = [];

  if (order.pizzas && Array.isArray(order.pizzas)) {
    order.pizzas.forEach((pizzaObj: any) => {
      const sName = pizzaObj.sizeName || pizzaObj.size || 'Tradicional';
      const slicesArr = pizzaObj.slices || pizzaObj.flavors || [];
      const qty = pizzaObj.quantity ?? 1;

      let addNames: any[] = [];
      if (pizzaObj.additionNames) {
        addNames = Array.isArray(pizzaObj.additionNames)
          ? pizzaObj.additionNames.map((n: any) => ({ id: Math.random().toString(), name: String(n) }))
          : Object.values(pizzaObj.additionNames).map((n: any) => ({ id: Math.random().toString(), name: String(n?.name || n) }));
      }

      let flavorStr = "";
      if (slicesArr.length > 0) {
        const firstS = slicesArr[0];
        const areAllIdentical = slicesArr.length > 1 && slicesArr.every((s: any) => {
          const f1 = s.flavorName || s.name || s.flavorId || s.id || "";
          const f2 = firstS.flavorName || firstS.name || firstS.flavorId || firstS.id || "";
          if (f1 !== f2) return false;
          const ing1 = (s.ingredients || []).slice().sort().join(',');
          const ing2 = (firstS.ingredients || []).slice().sort().join(',');
          return ing1 === ing2;
        });

        if (areAllIdentical && firstS) {
          let name = firstS.flavorName || firstS.name || firstS.flavorId || firstS.id || "";
          let ings = (firstS.ingredients && Array.isArray(firstS.ingredients) && firstS.ingredients.length > 0)
            ? ` (${firstS.ingredients.join(', ')})`
            : '';
          if (name === "build-your-own" || name === "Arma tu pizza" || name === "Personalizada") {
            name = "Personalizada" + ings;
          } else if (ings) {
            name = name + ings;
          }
          flavorStr = `🍕 ${name}`;
        } else {
          flavorStr = slicesArr.map((s: any) => {
            const portionMap: Record<string, string> = {
              whole: 'Entera', left: 'Mitad Izq.', right: 'Mitad Der.', 'top-left': '¼ Sup. Izq.', 'top-right': '¼ Sup. Der.', 'bottom-left': '¼ Inf. Izq.', 'bottom-right': '¼ Inf. Der.'
            };
            let p = s.portion ? (portionMap[s.portion] || s.portion) : "";
            let name = s.flavorName || s.name || s.id || "";
            let ings = (s.ingredients && Array.isArray(s.ingredients) && s.ingredients.length > 0)
              ? ` (${s.ingredients.join(', ')})`
              : '';
            if (name === "build-your-own" || name === "Arma tu pizza") {
              name = "Personalizada" + ings;
            } else if (ings) {
              name = name + ings;
            }
            return `${p}: 🍕 ${name}`;
          }).join('\n');
        }
      }

      products.push({
        productId: pizzaObj.id || Math.random().toString(),
        productName: sName && sName !== 'Tradicional' ? `Pizza ${sName}` : 'Pizza',
        variantId: '',
        variantLabel: flavorStr,
        price: pizzaObj.itemPrice || 0,
        quantity: qty,
        image: '',
        category: 'pizzas',
        completed: false,
        additions: addNames,
      } as OrderProduct);
    });
  }

  if (order.otherProductNames) {
    const qtys = order.otherProducts || {};
    const prices = order.otherProductPrices || {};
    Object.entries(order.otherProductNames).forEach(([id, name]) => {
      let itemPrice = prices[id] ?? 0;
      products.push({
        productId: id,
        productName: String(name),
        variantId: '',
        variantLabel: '',
        price: Number(itemPrice),
        quantity: Number(qtys[id] || 1),
        image: '',
        category: 'other',
        completed: false
      } as OrderProduct);
    });
  }

  if (order.additionNames) {
    const names = Array.isArray(order.additionNames)
      ? order.additionNames
      : Object.values(order.additionNames);

    names.forEach((add: any) => {
      const nameStr = typeof add === 'string' ? add : add?.name || 'Adición';
      products.push({
        productId: Math.random().toString(),
        productName: `ADICIÓN GENERAL: ${nameStr}`,
        variantId: '',
        variantLabel: '',
        price: 0,
        quantity: 1,
        image: '',
        category: 'addition',
        completed: false
      } as OrderProduct);
    });
  }

  return products;
}
